import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useLocation, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { Spectra } from "../typings";
import { decodeMacoLink } from "../utils/maco-link";

/**
 * The spectrum MaCo works on, wherever it comes from.
 *
 * With `:id` it is a stored spectrum, read as the pages always read it. On the
 * `/maco/peaks` routes it is the one carried in the link (utils/maco-link.ts).
 * Both arrive as the same `Spectra` shape, so the pages do not care which.
 *
 * `fromLink` is what the pages need to build their own links: a detail page
 * opened from a link spectrum must carry the fragment along, or it opens empty.
 */
export const useMacoSpectrum = () => {
  const { id } = useParams();
  const { hash } = useLocation();
  const [user, loading] = useAuthState(auth);
  const [spectra, setSpectra] = useState<Spectra | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const fromLink = id === undefined;

  useEffect(() => {
    if (!fromLink) return;
    const decoded = decodeMacoLink(hash);
    if ("error" in decoded) {
      setError(decoded.error);
      setSpectra(undefined);
    } else {
      setError(undefined);
      setSpectra(decoded.spectra);
    }
    setIsLoading(false);
  }, [fromLink, hash]);

  useEffect(() => {
    if (fromLink) return;
    if (loading) return;
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, "spectras", id as string), (snapshot) => {
      const data = snapshot.data() as Spectra | undefined;
      setSpectra(data ? { ...data, id: snapshot.id } : undefined);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [fromLink, user, loading, id]);

  return { spectra, isLoading, error, fromLink, hash };
};
